import { z } from "zod/v4";
import { venueIdSchema } from "./ids";

// An operator's request to link a venue to its canonical location (ADR-0054).
export const locationRequestIdSchema = z.uuid().brand<"LocationRequestId">();
export type LocationRequestId = z.infer<typeof locationRequestIdSchema>;

/** Bounds shared with the API (migration 388): characters, after trimming. */
export const LOCATION_REQUEST_TEXT_MAX = 500;

const requestBase = {
	createdAt: z.string(),
	id: locationRequestIdSchema,
	location: z.object({
		address: z.string().nullable(),
		city: z.string().nullable(),
		id: z.uuid(),
		name: z.string(),
	}),
	note: z.string().nullable(),
	requester: z.object({ email: z.string(), id: z.uuid() }),
	venue: z.object({ id: venueIdSchema, name: z.string(), slug: z.string() }),
};

/** Only a rejection carries a reason; only a decided request a decision time. */
export const locationRequestSchema = z.discriminatedUnion("status", [
	z.object({ ...requestBase, decidedAt: z.null(), reason: z.null(), status: z.literal("pending") }),
	z.object({
		...requestBase,
		decidedAt: z.string(),
		reason: z.null(),
		status: z.literal("approved"),
	}),
	z.object({
		...requestBase,
		decidedAt: z.string(),
		reason: z.string(),
		status: z.literal("rejected"),
	}),
]);
export type LocationRequest = z.infer<typeof locationRequestSchema>;
export type LocationRequestStatus = LocationRequest["status"];

export const createLocationRequestSchema = z.object({
	locationId: z.uuid(),
	note: z.string().trim().max(LOCATION_REQUEST_TEXT_MAX).nullable(),
});
export type CreateLocationRequest = z.infer<typeof createLocationRequestSchema>;

/** One hit of the location service's search (`GET /locations?q=`), cut to what a picker shows. */
export const locationCandidateSchema = z.object({
	address: z.string().nullish(),
	city: z.string().nullish(),
	id: z.uuid(),
	name: z.string(),
});
export type LocationCandidate = z.infer<typeof locationCandidateSchema>;
